import { Redirect, RouteProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { FC } from 'react';
import { useAtomValue } from 'jotai';
import { useNotification } from '@idk-web/core-ui';
import { ServiceType } from '@idk-web/api';
import { clearState, portalLoginStatus, portalSso } from '@/state/portalAuth';
import { SentryRoute } from '@/sentry';
import { useEventListener } from '@/hooks/portal/ws/useEventListener';
import { useCompany } from '@/hooks/portal/useCompany';
import { DEFAULT_NOTIFICATION_AUTO_HIDE_MS } from '@/config';
import routes from '@/components/portal/routes';

const EventAwareRoute: FC<RouteProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const company = useCompany();
  const notification = useNotification();
  const loginStatus = useAtomValue(portalLoginStatus);

  useEventListener(
    'SAFEMAIL_MESSAGE_RECEIVED',
    () => {
      if (company.services.some((s) => s.type === ServiceType.SAFEMAIL)) {
        notification.show(
          t('portal.notifications.safemail_message_received'),
          'success',
          DEFAULT_NOTIFICATION_AUTO_HIDE_MS,
        );
      }
    },
    [t, loginStatus, company],
  );

  return <SentryRoute {...props} />;
};

export type AuthenticatedRouteProps = RouteProps;

/**
 * A route which redirects to the login page if the current user is logged out.
 */
const AuthenticatedRoute: FC<AuthenticatedRouteProps> = (props) => {
  const loginStatus = useAtomValue(portalLoginStatus);
  const sso = useAtomValue(portalSso);

  switch (loginStatus) {
    case 'LOGGED_OUT':
      if (sso?.registrationId) {
        clearState();

        return (
          <Redirect
            to={routes.sessionExpiredSso.replace(
              ':registrationId',
              sso.registrationId,
            )}
          />
        );
      }

      return <Redirect to={routes.login} />;
    case 'AUTHENTICATED':
      return <EventAwareRoute {...props} />;
    default:
      return <SentryRoute {...props} />;
  }
};

export default AuthenticatedRoute;
